// src/TenantProfileForm.js  ver 03 from 02 Nov 5 2025 12:17am  (UPDATED for TenantProfileEditor role-based access)
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './App';

import * as userService from './api/userService';
import * as tenantService from './api/tenantService';
import { ApiError } from './api/apiClient';

const initialTenantProfileState = {
    Address: '',
    Country: '',
    SubsCoFullName: '',
    SubsCoId: '',
    SubsCoOfficialName: '',
    emailAddress: '',
    Website: '',
};

function TenantProfileForm() {
    const auth = useAuth();
    const navigate = useNavigate();

    const [tenantProfile, setTenantProfile] = useState(initialTenantProfileState);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isExistingTenant, setIsExistingTenant] = useState(false);
    const [tenantDocId, setTenantDocId] = useState(null);

    const userEmail = auth.user?.email;
    // NEW: Get TenantProfileEditor status from userProfile
    const canEditTenantProfile = auth.userProfile?.TenantProfileEditor === true;

    useEffect(() => {
        const fetchTenantProfile = async () => {
            if (!userEmail || !auth.user) {
                setError("No authenticated user email found.");
                setLoading(false);
                return;
            }

            setLoading(true);
            setError(null);
            try {
                const token = await auth.getIdToken();
                if (!token) {
                    setError("Authentication token not available.");
                    setLoading(false);
                    return;
                }

                // First, get the user's profile to see if a tenant is already associated
                const userProfileData = await userService.getUserProfile(userEmail, token);
                if (userProfileData && userProfileData.id && userProfileData.SubsCoDocId) {
                    // User has an associated tenant, fetch it
                    const tenantData = await tenantService.getTenantProfile(userProfileData.SubsCoDocId, token);
                    if (tenantData) {
                        setTenantProfile(tenantData);
                        setTenantDocId(userProfileData.SubsCoDocId);
                        setIsExistingTenant(true);
                    } else {
                        setError("Associated tenant profile not found in FoodTechAppTenants.");
                        setTenantProfile(prev => ({ ...prev, emailAddress: userEmail }));
                        setIsExistingTenant(false);
                    }
                } else {
                    // No associated tenant, prepare for new entry
                    setTenantProfile(prev => ({ ...prev, emailAddress: userEmail }));
                    setIsExistingTenant(false);
                }

            } catch (err) {
                if (err instanceof ApiError && err.status === 404) {
                    setTenantProfile(prev => ({ ...prev, emailAddress: userEmail }));
                    setIsExistingTenant(false);
                    console.log("Tenant or user profile not found (404), preparing to create new tenant.");
                } else {
                    console.error("Error fetching tenant profile:", err);
                    setError(`Failed to load tenant profile: ${err.message}`);
                }
            } finally {
                setLoading(false);
            }
        };

        fetchTenantProfile();
    }, [userEmail, auth]);

    const handleChange = (e) => {
        // Only allow changes if the user has edit permission
        if (!canEditTenantProfile) return;

        const { name, value } = e.target;
        setTenantProfile(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        // NEW: Do not proceed if user does not have edit permission
        if (!canEditTenantProfile) {
            setError("You do not have permission to edit this profile.");
            return;
        }

        if (!userEmail) {
            setError("Cannot save tenant profile: User email not available.");
            return;
        }
        if (!tenantProfile.SubsCoFullName || !tenantProfile.SubsCoId || !tenantProfile.Country) {
            setError("Company Full Name, Company ID, and Country are required.");
            return;
        }

        try {
            const token = await auth.getIdToken();
            if (!token) {
                setError("Authentication token not available.");
                return;
            }

            const payload = { ...tenantProfile, emailAddress: userEmail };

            if (isExistingTenant && tenantDocId) {
                await tenantService.updateTenantProfile(tenantDocId, payload, token);
                alert("Tenant profile updated successfully!");
            } else {
                await tenantService.addTenantProfile(payload, token);
                alert("Tenant profile created and associated with your user!");
                auth.refreshUserProfile();
            }
            navigate('/inventory');
        } catch (err) {
            console.error("Error saving tenant profile:", err);
            setError(`Failed to save tenant profile: ${err.message}`);
        }
    };

    if (loading) return <p>Loading tenant profile...</p>;
    if (error) return <p style={{ color: 'red' }}>Error: {error}</p>;
    if (!userEmail) return <p style={{ color: 'red' }}>Error: User must be logged in to manage tenant profile.</p>;


    return (
        <div className="container form-container">
            <h2>{isExistingTenant ? 'Edit Company Profile' : 'Create Company Profile'}</h2>
            {!canEditTenantProfile && isExistingTenant && (
                <p style={{ color: 'orange', marginBottom: '1rem' }}>You are viewing this profile in read-only mode. Contact an administrator to gain editing permissions.</p>
            )}
            <form onSubmit={handleSubmit}>
                <div className="form-group">
                    <label>Company Full Name *</label>
                    <input type="text" name="SubsCoFullName" value={tenantProfile.SubsCoFullName} onChange={handleChange} required disabled={!canEditTenantProfile} />
                </div>
                <div className="form-group">
                    <label>Company ID *</label>
                    <input type="text" name="SubsCoId" value={tenantProfile.SubsCoId} onChange={handleChange} required disabled={!canEditTenantProfile} />
                </div>
                <div className="form-group">
                    <label>Country *</label>
                    <input type="text" name="Country" value={tenantProfile.Country} onChange={handleChange} required disabled={!canEditTenantProfile} />
                </div>
                <div className="form-group">
                    <label>Official Name</label>
                    <input type="text" name="SubsCoOfficialName" value={tenantProfile.SubsCoOfficialName} onChange={handleChange} disabled={!canEditTenantProfile} />
                </div>
                <div className="form-group">
                    <label>Address</label>
                    <input type="text" name="Address" value={tenantProfile.Address} onChange={handleChange} disabled={!canEditTenantProfile} />
                </div>
                <div className="form-group">
                    <label>Email Address</label>
                    <input type="email" name="emailAddress" value={tenantProfile.emailAddress} disabled /> {/* Always disabled */}
                </div>
                <div className="form-group">
                    <label>Website</label>
                    <input type="url" name="Website" value={tenantProfile.Website} onChange={handleChange} disabled={!canEditTenantProfile} />
                </div>
                
                {canEditTenantProfile && <button type="submit">Save Company Profile</button>} {/* NEW: Conditionally show save button */}
                
            </form>
        </div>
    );
}

export default TenantProfileForm;